import { OcrStatesEnum, ProcessEmployeeIdCardInput } from '@vivaldis/graphql'
import type { RcFile } from 'antd/es/upload/interface'
import { Formik, FormikContextType, useFormikContext } from 'formik'
import { FC, PropsWithChildren, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { fileToBase64 } from '../../../../../utils/fileToBase64'
import { Step, useStep } from '../../../provider/StepProvider'
import { Employee } from '../../../types/Employee'

export interface UploadIdCardFormValues {
  idCardFrontPictureFile: RcFile | null
  idCardBackPictureFile: RcFile | null
}

export function useForm(): FormikContextType<UploadIdCardFormValues> {
  return useFormikContext()
}

export interface EmployeeFormProviderProps {
  employee: Employee
  onSubmit: (
    processEmployeeIdCardInput: ProcessEmployeeIdCardInput
  ) => Promise<Employee | undefined>
}

export const FormProvider: FC<PropsWithChildren<EmployeeFormProviderProps>> = ({
  employee,
  onSubmit,
  children
}) => {
  const [t] = useTranslation('web_ui')

  const { setStep } = useStep()

  const initialFormValues = {
    idCardFrontPictureFile: null,
    idCardBackPictureFile: null
  }

  const ValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        idCardFrontPictureFile: Yup.mixed()
          .label(
            t(
              'edit_employee_personal_information_form.steps.upload_id_card.id_card_front_picture.label'
            )
          )
          .nullable()
          .required(
            t(
              'edit_employee_personal_information_form.steps.upload_id_card.id_card_front_picture.errors.required'
            )
          ),
        idCardBackPictureFile: Yup.mixed()
          .label(
            t(
              'edit_employee_personal_information_form.steps.upload_id_card.id_card_back_picture.label'
            )
          )
          .nullable()
          .required(
            t(
              'edit_employee_personal_information_form.steps.upload_id_card.id_card_back_picture.errors.required'
            )
          )
      }),
    [t]
  )

  const handleSubmit = useCallback(
    async (values: UploadIdCardFormValues) => {
      if (!values.idCardBackPictureFile || !values.idCardFrontPictureFile) {
        return setStep(Step.OcrFailedStep)
      }

      const employeeAfterOCR = await onSubmit({
        employeeId: employee.id,
        backPicture: {
          fileName: values.idCardBackPictureFile.name,
          base64: await fileToBase64(values.idCardBackPictureFile)
        },
        frontPicture: {
          fileName: values.idCardFrontPictureFile.name,
          base64: await fileToBase64(values.idCardFrontPictureFile)
        }
      })

      if (!employeeAfterOCR) {
        return setStep(Step.OcrFailedStep)
      }

      if (employeeAfterOCR.ocrState === OcrStatesEnum.FAILED) {
        return setStep(Step.OcrFailedStep)
      }

      if (employeeAfterOCR.ocrState === OcrStatesEnum.UNDEFINED) {
        return setStep(Step.OcrFailedStep)
      }

      if (employeeAfterOCR.ocrState === OcrStatesEnum.SUCCESS) {
        // if not all fields are filled after OCR, go to a manual edit step
        // check for main fields
        if (
          !employeeAfterOCR.firstName ||
          !employeeAfterOCR.lastName ||
          !employeeAfterOCR.profile?.nationalityCountry ||
          !employeeAfterOCR.ssn
        ) {
          return setStep(Step.EditManuallyStep)
        }

        // check for fields for EEA member countries
        if (employeeAfterOCR.profile?.nationalityCountry.eeaMember) {
          if (
            !employeeAfterOCR.profile?.idCardFrontPicture ||
            !employeeAfterOCR.profile?.idCardBackPicture ||
            !employeeAfterOCR.profile?.idCardNumber ||
            !employeeAfterOCR.profile?.idCardExpiresOn
          ) {
            return setStep(Step.EditManuallyStep)
          }
        }

        // check for fields for none-EEA member countries
        if (!employeeAfterOCR.profile?.nationalityCountry.eeaMember) {
          if (
            !employeeAfterOCR.workProfile?.residencePermitFrontPicture ||
            !employeeAfterOCR.workProfile?.residencePermitBackPicture ||
            !employeeAfterOCR.workProfile?.residencePermitCode ||
            !employeeAfterOCR.workProfile?.residencePermitExpiresOn
          ) {
            return setStep(Step.EditManuallyStep)
          }
        }

        return setStep(Step.OcrSucceededStep)
      }

      return setStep(Step.OcrFailedStep)
    },
    [employee.id, onSubmit, setStep]
  )

  return (
    <Formik<UploadIdCardFormValues>
      initialValues={initialFormValues}
      validateOnChange={false}
      validateOnBlur={true}
      validationSchema={ValidationSchema}
      onSubmit={handleSubmit}
    >
      {children}
    </Formik>
  )
}
